import { addMonths, format, parse } from 'date-fns';
import { and, asc, desc, eq, inArray, isNull, notInArray, sql } from 'drizzle-orm';
import { db, runInTransaction } from '@/db/client';
import {
    accounts,
    categories,
    financingAdjustments,
    financings,
    subcategories,
    transactions,
} from '@/db/schema';
import { generateId } from '@/lib/id';
import { resolveTransactionTime } from '@/lib/transactionTime';
import { FinancingEntryType, FinancingKind, type FinancingKindValue } from '@/lib/constants';
import { roundCurrency as round2 } from '@/lib/installments';
import {
    getInstallmentBaseAmount,
    planAdvanceOnNextInstallments,
    type AdvancePlan,
} from '@/lib/financingInstallments';
import {
    AmortizationSystem,
    getContractInstallmentAmount,
    hasDerivedCurve,
    isAmortizationSystem,
    recalibrateExtraAmount,
    type AmortizationSystemValue,
    type FinancingCurve,
} from '@/lib/financingCurve';
import { recalculateAccountBalance } from './accountRepository';
import { setFinancingTags, syncFinancingTransactionTags } from './tagRepository';
import { ExpectedError } from '@/lib/errorUtils';
import i18n from '@/lib/i18n';
import { compareIso } from '@/lib/compareIso';

export type Financing = typeof financings.$inferSelect;
export type FinancingAdjustment = typeof financingAdjustments.$inferSelect;
export type FinancingStatus = 'active' | 'settled' | 'archived';

export interface FinancingWithRelations extends Financing {
    paymentAccountName?: string | null;
    paymentAccountColor?: string | null;
    paymentAccountIcon?: string | null;
    categoryName?: string | null;
    categoryColor?: string | null;
    categoryIcon?: string | null;
    subcategoryName?: string | null;
    remainingInstallments: number;
    nextPendingInstallmentNumber: number | null;
    nextPendingDueDate: string | null;
}

export interface FinancingAdjustmentWithRelations extends FinancingAdjustment {
    transactionDate?: string | null;
    transactionDescription?: string | null;
}

type FinancingScheduleRow = {
    id: string;
    financingId: string | null;
    date: string;
    /** O que a parcela cobra HOJE — com parcial descontado e encargos somados. */
    amount: number;
    /** Desempata parcelas do mesmo vencimento no planejador de adiantamento. */
    createdAt: string;
    isConfirmed: number;
    financingInstallmentNumber: number | null;
};

type FinancingBaseRow = Financing & {
    paymentAccountName?: string | null;
    paymentAccountColor?: string | null;
    paymentAccountIcon?: string | null;
    categoryName?: string | null;
    categoryColor?: string | null;
    categoryIcon?: string | null;
    subcategoryName?: string | null;
};

type NewFinancingInput = {
    name: string;
    /** 'financing' (padrão) ou 'loan' — só muda rótulo/copy, ver ADR 0055. */
    kind?: FinancingKindValue;
    institution?: string | null;
    paymentAccountId: string;
    categoryId: string;
    subcategoryId?: string | null;
    monthlyAmount: number;
    totalInstallments: number;
    nextInstallmentNumber: number;
    nextDueDate: string;
    lastKnownBalance?: number | null;
    /** Tags do contrato — aplicadas a todos os lançamentos que ele gera. */
    tagIds?: string[];
    /** Curva da parcela (ADR 0081). Ausente = `fixed`: a série nasce achatada, como antes. */
    amortizationSystem?: AmortizationSystemValue;
    /** Taxa do contrato, AO ANO (8.99 = 8,99% a.a.). */
    annualRate?: number | null;
    /** Saldo base da derivação; cai no `lastKnownBalance` quando não informado. */
    curveBalance?: number | null;
    /** Seguros e taxas: adicional fixo por mês. */
    curveExtraAmount?: number | null;
};

/**
 * Os três destinos possíveis de um pagamento extra:
 * - `term`: remove as ÚLTIMAS parcelas pendentes, valor mensal intacto;
 * - `installment`: reescreve o valor de TODAS as pendentes, prazo intacto;
 * - `advance`: consome as PRÓXIMAS parcelas, valor mensal e prazo do resto intactos.
 *
 * `installment` pressupõe alguém recalculando a tabela do outro lado — por isso só o
 * financiamento o oferece; o empréstimo de parcela fixa usa `advance` (ADR 0057).
 */
export type FinancingReductionMode = 'term' | 'installment' | 'advance';

type RecordFinancingPrepaymentInput = {
    financingId: string;
    date: string;
    amount: number;
    remainingInstallments: number;
    newOutstandingBalance: number;
    notes?: string | null;
    /**
     * Ausente = leitura antiga, inferida de `newMonthlyAmount` (parcela × prazo).
     * Mantida porque `advance` foi o terceiro modo a chegar e não podia mudar
     * a assinatura de quem já chamava.
     */
    reductionMode?: FinancingReductionMode;
    /** Amortização que reduz o VALOR das parcelas pendentes mantendo o prazo. */
    newMonthlyAmount?: number | null;
    /**
     * Abatimento sem movimentar conta (ex.: FGTS no financiamento imobiliário): grava
     * só o evento no razão do financiamento, sem transação de despesa — nenhum saldo
     * de conta é afetado. O desfazer já trata transactionId nulo.
     */
    withoutCashMovement?: boolean;
};

type UpdateFinancingInput = {
    financingId: string;
    name: string;
    /** Trocar financiamento ↔ empréstimo — ausente = não mexer. */
    kind?: FinancingKindValue;
    institution?: string | null;
    paymentAccountId: string;
    monthlyAmount: number;
    /** Vencimento da próxima parcela pendente — re-data as demais em sequência mensal. */
    nextDueDate?: string | null;
    /** Novo total do contrato — só permitido antes de qualquer pagamento ou abatimento. */
    totalInstallments?: number | null;
    /** Novo conjunto de tags do contrato — ausente = não mexer; [] = limpar. */
    tagIds?: string[];
    /**
     * Nova categoria do contrato — ausente = não mexer. Propaga para TODOS os lançamentos
     * vinculados (parcelas pagas e pendentes, abatimentos), igual ao nome: a categoria é
     * propriedade do contrato, e deixar metade da série na categoria antiga só empurraria
     * o erro para os relatórios.
     */
    categoryId?: string;
    /** Subcategoria da nova categoria — ignorada (vira null) se não pertencer a ela. */
    subcategoryId?: string | null;
    /**
     * Curva da parcela (ADR 0081) — ausente = não mexer. É por aqui que um contrato
     * cadastrado como parcela fixa por falta de opção vira SAC: a série pendente é
     * regenerada a partir da curva nova.
     */
    amortizationSystem?: AmortizationSystemValue;
    annualRate?: number | null;
    curveBalance?: number | null;
    curveExtraAmount?: number | null;
};

function combineConditions<T>(conditions: T[]) {
    if (conditions.length === 0) return undefined;
    if (conditions.length === 1) return conditions[0];
    return and(...conditions as any);
}

function normalizeOptionalId(value?: string | null): string | null {
    if (!value) return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}

function formatInstallmentDate(baseDate: string, offset: number) {
    return format(
        addMonths(parse(baseDate, 'yyyy-MM-dd', new Date()), offset),
        'yyyy-MM-dd',
    );
}

async function sanitizeFinancingReferenceIds(data: {
    paymentAccountId: string;
    categoryId: string;
    subcategoryId?: string | null;
}) {
    const paymentAccountId = normalizeOptionalId(data.paymentAccountId);
    const categoryId = normalizeOptionalId(data.categoryId);
    let subcategoryId = normalizeOptionalId(data.subcategoryId);

    if (!paymentAccountId) {
        throw new ExpectedError(i18n.t('ui:errors.financingPaymentAccountRequired'));
    }

    if (!categoryId) {
        throw new ExpectedError(i18n.t('ui:errors.financingCategoryRequired'));
    }

    const [accountRows, categoryRows, subcategoryRows] = await Promise.all([
        db.select({ id: accounts.id })
            .from(accounts)
            .where(and(eq(accounts.id, paymentAccountId), isNull(accounts.deletedAt))),
        db.select({ id: categories.id, type: categories.type })
            .from(categories)
            .where(and(eq(categories.id, categoryId), isNull(categories.deletedAt))),
        subcategoryId
            ? db.select({ id: subcategories.id, categoryId: subcategories.categoryId })
                .from(subcategories)
                .where(and(eq(subcategories.id, subcategoryId), isNull(subcategories.deletedAt)))
            : Promise.resolve([]),
    ]);

    if (accountRows.length === 0) {
        throw new ExpectedError(i18n.t('ui:errors.accountGone'));
    }

    if (categoryRows.length === 0 || categoryRows[0].type !== 'expense') {
        throw new ExpectedError(i18n.t('ui:errors.categoryGone'));
    }

    const resolvedSubcategory = subcategoryRows[0];
    if (!resolvedSubcategory || resolvedSubcategory.categoryId !== categoryId) {
        subcategoryId = null;
    }

    return {
        paymentAccountId,
        categoryId,
        subcategoryId,
    };
}

async function getRegularScheduleRows(financingId: string): Promise<FinancingScheduleRow[]> {
    return db
        .select({
            id: transactions.id,
            financingId: transactions.financingId,
            date: transactions.date,
            amount: transactions.amount,
            createdAt: transactions.createdAt,
            isConfirmed: transactions.isConfirmed,
            financingInstallmentNumber: transactions.financingInstallmentNumber,
        })
        .from(transactions)
        .where(and(
            eq(transactions.financingId, financingId),
            eq(transactions.financingEntryType, FinancingEntryType.REGULAR_INSTALLMENT),
            isNull(transactions.deletedAt),
        ))
        .orderBy(asc(transactions.date), asc(transactions.createdAt));
}

/**
 * Quanto já foi pago parcialmente em cada número de parcela. É o que impede o
 * valor pendente de "voltar ao cheio" quando o contrato é reescrito: a parcela 3
 * que recebeu R$ 17,33 deve continuar devendo `mensal - 17,33`, não `mensal`.
 */
async function getPartialPaidByInstallment(financingId: string): Promise<Map<number, number>> {
    const rows = await db
        .select({
            amount: transactions.amount,
            financingInstallmentNumber: transactions.financingInstallmentNumber,
        })
        .from(transactions)
        .where(and(
            eq(transactions.financingId, financingId),
            eq(transactions.financingEntryType, FinancingEntryType.PARTIAL_PAYMENT),
            isNull(transactions.deletedAt),
        ));

    const map = new Map<number, number>();
    for (const row of rows) {
        if (row.financingInstallmentNumber == null) continue;
        map.set(
            row.financingInstallmentNumber,
            round2((map.get(row.financingInstallmentNumber) ?? 0) + row.amount),
        );
    }
    return map;
}

/**
 * Quantas parcelas cabem num UPDATE ... CASE. Cada `WHEN` gasta 2 parâmetros e o SQLite
 * para em 999 — num contrato de 360 parcelas isso vira 2 statements em vez de 360
 * chamadas JSI, que é a diferença entre instantâneo e travar a tela.
 */
const CURVE_UPDATE_CHUNK = 200;

/**
 * Reescreve o valor das parcelas PENDENTES a partir do contrato, descontando o que já foi
 * pago parcialmente em cada uma. Usado quando o valor de contrato muda (edição, abatimento
 * que reduz parcela, recalibração) — os encargos manuais daquela parcela são perdidos de
 * propósito: o valor base mudou, o acréscimo antigo não vale mais.
 *
 * Com **Curva da parcela** (ADR 0081) cada parcela tem o SEU valor, então o caminho é um
 * CASE por número de parcela; sem curva segue o UPDATE em lote de sempre.
 */
async function applyPendingInstallmentAmounts(
    financingId: string,
    curve: FinancingCurve,
    now: string,
    extraPatch: Record<string, unknown> = {},
) {
    const partialPaid = await getPartialPaidByInstallment(financingId);

    if (hasDerivedCurve(curve)) {
        await applyCurveInstallmentAmounts(financingId, curve, partialPaid, now, extraPatch);
        return;
    }

    const monthlyAmount = curve.monthlyAmount;
    const touchedNumbers = [...partialPaid.keys()];

    // Um UPDATE em lote cobre todas as parcelas SEM pagamento parcial — que é o caso de
    // 100% delas no contrato comum e de quase todas num contrato de 360 parcelas. Só as
    // que têm desconto próprio caem no laço abaixo, uma linha cada.
    await db
        .update(transactions)
        .set({ ...extraPatch, amount: monthlyAmount, updatedAt: now })
        .where(and(
            eq(transactions.financingId, financingId),
            eq(transactions.financingEntryType, FinancingEntryType.REGULAR_INSTALLMENT),
            eq(transactions.isConfirmed, 0),
            isNull(transactions.deletedAt),
            ...(touchedNumbers.length > 0
                ? [notInArray(transactions.financingInstallmentNumber, touchedNumbers)]
                : []),
        ));

    for (const [installmentNumber, alreadyPaid] of partialPaid) {
        await db
            .update(transactions)
            .set({
                ...extraPatch,
                amount: getInstallmentBaseAmount(monthlyAmount, alreadyPaid),
                updatedAt: now,
            })
            .where(and(
                eq(transactions.financingId, financingId),
                eq(transactions.financingEntryType, FinancingEntryType.REGULAR_INSTALLMENT),
                eq(transactions.financingInstallmentNumber, installmentNumber),
                eq(transactions.isConfirmed, 0),
                isNull(transactions.deletedAt),
            ));
    }
}

/**
 * Reescreve as pendentes com o valor DERIVADO da curva para o número de cada parcela.
 * Só toca em pendente: valor de parcela paga é fato histórico.
 */
async function applyCurveInstallmentAmounts(
    financingId: string,
    curve: FinancingCurve,
    partialPaid: Map<number, number>,
    now: string,
    extraPatch: Record<string, unknown> = {},
) {
    // O patch de nome/conta/categoria vai primeiro e em UMA tacada, para alcançar também a
    // pendente sem número de parcela (linha legada): ela não tem valor derivável, mas não
    // pode ficar com o nome e a conta antigos enquanto o resto da série acompanha.
    if (Object.keys(extraPatch).length > 0) {
        await db
            .update(transactions)
            .set({ ...extraPatch, updatedAt: now })
            .where(and(
                eq(transactions.financingId, financingId),
                eq(transactions.financingEntryType, FinancingEntryType.REGULAR_INSTALLMENT),
                eq(transactions.isConfirmed, 0),
                isNull(transactions.deletedAt),
            ));
    }

    const pendingRows = (await getRegularScheduleRows(financingId))
        .filter((row) => row.isConfirmed === 0 && row.financingInstallmentNumber != null);

    if (pendingRows.length === 0) return;

    for (let start = 0; start < pendingRows.length; start += CURVE_UPDATE_CHUNK) {
        const chunk = pendingRows.slice(start, start + CURVE_UPDATE_CHUNK);
        const branches = chunk.map((row) => {
            const installmentNumber = row.financingInstallmentNumber!;
            const contractAmount = getContractInstallmentAmount(curve, installmentNumber);
            const alreadyPaid = partialPaid.get(installmentNumber) ?? 0;
            return sql`WHEN ${installmentNumber} THEN ${getInstallmentBaseAmount(contractAmount, alreadyPaid)}`;
        });

        await db
            .update(transactions)
            .set({
                amount: sql`CASE ${transactions.financingInstallmentNumber} ${sql.join(branches, sql` `)} ELSE ${transactions.amount} END`,
                updatedAt: now,
            })
            .where(and(
                inArray(transactions.id, chunk.map((row) => row.id)),
                isNull(transactions.deletedAt),
            ));
    }
}

/**
 * Os parâmetros da curva como o contrato os guarda. `Financing` já é estruturalmente um
 * `FinancingCurve` — a função existe para nomear a intenção nos pontos de uso.
 */
function toCurve(financing: Financing): FinancingCurve {
    return financing;
}

/**
 * Resolve o sistema de amortização vindo do input. Valor desconhecido cai em `fixed` —
 * inventar uma curva a partir de lixo seria pior que a série achatada de hoje.
 */
function resolveAmortizationSystem(value: unknown): AmortizationSystemValue {
    return isAmortizationSystem(value) ? value : AmortizationSystem.FIXED;
}

function buildDerivedState(rows: FinancingScheduleRow[]) {
    const pendingRows = rows.filter((row) => row.isConfirmed === 0);
    const nextPending = pendingRows[0];

    return {
        remainingInstallments: pendingRows.length,
        nextPendingInstallmentNumber: nextPending?.financingInstallmentNumber ?? null,
        nextPendingDueDate: nextPending?.date ?? null,
    };
}

async function buildDerivedStateMap(financingIds: string[]) {
    const derivedMap = new Map<string, ReturnType<typeof buildDerivedState>>();
    if (financingIds.length === 0) return derivedMap;

    const rows = await db
        .select({
            id: transactions.id,
            financingId: transactions.financingId,
            date: transactions.date,
            amount: transactions.amount,
            createdAt: transactions.createdAt,
            isConfirmed: transactions.isConfirmed,
            financingInstallmentNumber: transactions.financingInstallmentNumber,
        })
        .from(transactions)
        .where(and(
            inArray(transactions.financingId, financingIds),
            eq(transactions.financingEntryType, FinancingEntryType.REGULAR_INSTALLMENT),
            isNull(transactions.deletedAt),
        ))
        .orderBy(asc(transactions.date), asc(transactions.createdAt));

    const rowsByFinancing = new Map<string, FinancingScheduleRow[]>();
    for (const row of rows) {
        if (!row.financingId) continue;
        const current = rowsByFinancing.get(row.financingId) ?? [];
        current.push(row);
        rowsByFinancing.set(row.financingId, current);
    }

    for (const financingId of financingIds) {
        derivedMap.set(financingId, buildDerivedState(rowsByFinancing.get(financingId) ?? []));
    }

    return derivedMap;
}

function attachDerivedState(
    financingRows: FinancingBaseRow[],
    derivedMap: Map<string, ReturnType<typeof buildDerivedState>>,
): FinancingWithRelations[] {
    return financingRows.map((row) => {
        const derived = derivedMap.get(row.id) ?? {
            remainingInstallments: 0,
            nextPendingInstallmentNumber: null,
            nextPendingDueDate: null,
        };

        return {
            ...row,
            ...derived,
        };
    });
}

async function getFinancingBaseRows(
    whereClause?: any,
): Promise<FinancingBaseRow[]> {
    const query = db
        .select({
            id: financings.id,
            name: financings.name,
            kind: financings.kind,
            institution: financings.institution,
            paymentAccountId: financings.paymentAccountId,
            categoryId: financings.categoryId,
            subcategoryId: financings.subcategoryId,
            monthlyAmount: financings.monthlyAmount,
            totalInstallments: financings.totalInstallments,
            amortizationSystem: financings.amortizationSystem,
            annualRate: financings.annualRate,
            curveBalance: financings.curveBalance,
            curveInstallmentNumber: financings.curveInstallmentNumber,
            curveExtraAmount: financings.curveExtraAmount,
            nextInstallmentNumber: financings.nextInstallmentNumber,
            nextDueDate: financings.nextDueDate,
            lastKnownBalance: financings.lastKnownBalance,
            balanceUpdatedAt: financings.balanceUpdatedAt,
            status: financings.status,
            createdAt: financings.createdAt,
            updatedAt: financings.updatedAt,
            paymentAccountName: accounts.name,
            paymentAccountColor: accounts.color,
            paymentAccountIcon: accounts.icon,
            categoryName: categories.name,
            categoryColor: categories.color,
            categoryIcon: categories.icon,
            subcategoryName: subcategories.name,
        })
        .from(financings)
        .leftJoin(accounts, eq(financings.paymentAccountId, accounts.id))
        .leftJoin(categories, eq(financings.categoryId, categories.id))
        .leftJoin(subcategories, eq(financings.subcategoryId, subcategories.id));

    const orderedQuery = (whereClause ? query.where(whereClause) : query)
        .orderBy(
            asc(financings.status),
            asc(financings.nextDueDate),
            asc(financings.name),
        );

    return await orderedQuery;
}

export async function syncFinancingState(financingId: string): Promise<void> {
    const [financing] = await db
        .select()
        .from(financings)
        .where(eq(financings.id, financingId));

    if (!financing) return;

    const derived = buildDerivedState(await getRegularScheduleRows(financingId));
    const nextStatus: FinancingStatus = financing.status === 'archived'
        ? 'archived'
        : derived.remainingInstallments > 0
            ? 'active'
            : 'settled';

    await db
        .update(financings)
        .set({
            nextInstallmentNumber: derived.nextPendingInstallmentNumber,
            nextDueDate: derived.nextPendingDueDate,
            status: nextStatus,
            updatedAt: new Date().toISOString(),
        })
        .where(eq(financings.id, financingId));

    await syncFinancingTransactionTags(financingId);
}

export async function getFinancings(
    statuses: FinancingStatus[] = ['active'],
): Promise<FinancingWithRelations[]> {
    const conditions = statuses.length > 0
        ? [inArray(financings.status, statuses)]
        : [];

    const financingRows = await getFinancingBaseRows(combineConditions(conditions));
    const derivedMap = await buildDerivedStateMap(financingRows.map((row) => row.id));
    return attachDerivedState(financingRows, derivedMap);
}

export async function getFinancingById(id: string): Promise<FinancingWithRelations | null> {
    const financingRows = await getFinancingBaseRows(eq(financings.id, id));
    if (!financingRows[0]) return null;

    const derivedMap = await buildDerivedStateMap([id]);
    return attachDerivedState(financingRows, derivedMap)[0] ?? null;
}

export async function getFinancingAdjustments(
    financingId: string,
): Promise<FinancingAdjustmentWithRelations[]> {
    return db
        .select({
            id: financingAdjustments.id,
            financingId: financingAdjustments.financingId,
            date: financingAdjustments.date,
            amount: financingAdjustments.amount,
            newOutstandingBalance: financingAdjustments.newOutstandingBalance,
            remainingInstallments: financingAdjustments.remainingInstallments,
            notes: financingAdjustments.notes,
            transactionId: financingAdjustments.transactionId,
            createdAt: financingAdjustments.createdAt,
            transactionDate: transactions.date,
            transactionDescription: transactions.description,
        })
        .from(financingAdjustments)
        .leftJoin(transactions, eq(financingAdjustments.transactionId, transactions.id))
        .where(eq(financingAdjustments.financingId, financingId))
        .orderBy(desc(financingAdjustments.date), desc(financingAdjustments.createdAt));
}

export async function createFinancing(data: NewFinancingInput): Promise<FinancingWithRelations> {
    if (!data.paymentAccountId) {
        throw new ExpectedError(i18n.t('ui:errors.financingPaymentAccountRequired'));
    }
    if (!data.monthlyAmount || data.monthlyAmount <= 0) {
        throw new ExpectedError(i18n.t('ui:errors.financingMonthlyAmountInvalid'));
    }
    if (!data.nextInstallmentNumber || data.nextInstallmentNumber < 1) {
        throw new ExpectedError(i18n.t('ui:errors.nextInstallmentRequired'));
    }
    if (!data.nextDueDate) {
        throw new ExpectedError(i18n.t('ui:errors.nextDueDateRequired'));
    }
    if (data.nextInstallmentNumber > data.totalInstallments) {
        throw new ExpectedError(i18n.t('ui:errors.nextInstallmentExceedsTotal'));
    }

    const refs = await sanitizeFinancingReferenceIds(data);
    const financingId = generateId();
    const now = new Date().toISOString();

    // A parcela de referência da curva é a próxima do contrato: é dela em diante que o
    // saldo informado vale. O saldo cai no `lastKnownBalance` quando não vem separado —
    // no cadastro os dois são o mesmo número, e pedir duas vezes seria ruído.
    const curve: FinancingCurve = {
        amortizationSystem: resolveAmortizationSystem(data.amortizationSystem),
        monthlyAmount: data.monthlyAmount,
        totalInstallments: data.totalInstallments,
        annualRate: data.annualRate ?? null,
        curveBalance: data.curveBalance ?? data.lastKnownBalance ?? null,
        curveInstallmentNumber: data.nextInstallmentNumber,
        curveExtraAmount: data.curveExtraAmount ?? null,
    };

    await db.insert(financings).values({
        id: financingId,
        name: data.name.trim(),
        kind: data.kind ?? FinancingKind.FINANCING,
        institution: normalizeOptionalId(data.institution) ?? null,
        paymentAccountId: refs.paymentAccountId,
        categoryId: refs.categoryId,
        subcategoryId: refs.subcategoryId,
        monthlyAmount: data.monthlyAmount,
        totalInstallments: data.totalInstallments,
        amortizationSystem: curve.amortizationSystem!,
        annualRate: curve.annualRate,
        curveBalance: curve.curveBalance,
        curveInstallmentNumber: curve.curveInstallmentNumber,
        curveExtraAmount: curve.curveExtraAmount,
        nextInstallmentNumber: data.nextInstallmentNumber,
        nextDueDate: data.nextDueDate,
        lastKnownBalance: data.lastKnownBalance ?? null,
        balanceUpdatedAt: data.lastKnownBalance != null ? format(new Date(), 'yyyy-MM-dd') : null,
        status: 'active',
        createdAt: now,
        updatedAt: now,
    });

    const transactionRows = [];
    for (let installmentNumber = data.nextInstallmentNumber; installmentNumber <= data.totalInstallments; installmentNumber += 1) {
        const offset = installmentNumber - data.nextInstallmentNumber;
        transactionRows.push({
            id: generateId(),
            type: 'expense',
            // Sem curva devolve o valor mensal — a série achatada de sempre.
            amount: getContractInstallmentAmount(curve, installmentNumber),
            description: data.name.trim(),
            date: formatInstallmentDate(data.nextDueDate, offset),
            // Parcela nasce AGENDADA: a hora só entra quando ela for paga (ver
            // updateSingleTransaction) — "agora" numa parcela que vence em março é mentira.
            time: null,
            notes: null,
            accountId: refs.paymentAccountId,
            creditCardId: null,
            invoiceId: null,
            transferDestinationAccountId: null,
            transferId: null,
            categoryId: refs.categoryId,
            subcategoryId: refs.subcategoryId,
            isRecurring: 0,
            recurringId: null,
            installmentId: null,
            installmentNumber: null,
            installmentTotal: null,
            financingId,
            financingEntryType: FinancingEntryType.REGULAR_INSTALLMENT,
            financingInstallmentNumber: installmentNumber,
            adjustmentType: null,
            isConfirmed: 0,
            isHiddenFromSubs: 0,
            createdAt: now,
            updatedAt: now,
            deletedAt: null,
        });
    }

    if (transactionRows.length > 0) {
        await db.insert(transactions).values(transactionRows);
    }

    if (data.tagIds && data.tagIds.length > 0) {
        // Mesmo caminho do editar (bulk + valida tags vivas) — sem segunda
        // semântica de "aplicar tags do contrato".
        await setFinancingTags(financingId, data.tagIds);
    }

    return (await getFinancingById(financingId))!;
}

/**
 * Edição estrutural do contrato: nome, instituição, conta de pagamento, valor da
 * parcela mensal, vencimento e total de parcelas. O novo valor/conta vale só para
 * as parcelas PENDENTES (as pagas são fato histórico); o nome propaga para todos
 * os lançamentos vinculados. Nova data re-data as pendentes em sequência mensal.
 * O total de parcelas só pode mudar num contrato sem pagamento/abatimento — aí a
 * série inteira é regenerada a partir do vencimento informado.
 */
export async function updateFinancing(data: UpdateFinancingInput): Promise<FinancingWithRelations> {
    const financing = await getFinancingById(data.financingId);
    if (!financing) {
        throw new Error('Financiamento nao encontrado.');
    }

    const name = data.name.trim();
    if (!name) {
        throw new ExpectedError(i18n.t('ui:errors.contractNameRequired'));
    }
    if (!data.monthlyAmount || data.monthlyAmount <= 0) {
        throw new ExpectedError(i18n.t('ui:errors.financingMonthlyAmountInvalid'));
    }

    const paymentAccountId = normalizeOptionalId(data.paymentAccountId);
    if (!paymentAccountId) {
        throw new ExpectedError(i18n.t('ui:errors.financingPaymentAccountRequired'));
    }
    const accountRows = await db
        .select({ id: accounts.id })
        .from(accounts)
        .where(and(eq(accounts.id, paymentAccountId), isNull(accounts.deletedAt)));
    if (accountRows.length === 0) {
        throw new ExpectedError(i18n.t('ui:errors.accountGone'));
    }

    const now = new Date().toISOString();

    // Categoria: só valida/propaga quando veio no input (o form de edição sempre manda;
    // chamadas antigas sem o campo seguem intocando o contrato). Reaproveita o sanitize
    // da criação — categoria viva, do tipo despesa, e subcategoria coerente com ela.
    const wantsCategoryChange = data.categoryId != null;
    const nextRefs = wantsCategoryChange
        ? await sanitizeFinancingReferenceIds({
            paymentAccountId,
            categoryId: data.categoryId!,
            subcategoryId: data.subcategoryId,
        })
        : null;
    const nextCategoryId = nextRefs?.categoryId ?? financing.categoryId;
    const nextSubcategoryId = nextRefs ? nextRefs.subcategoryId : financing.subcategoryId;

    // Mudança estrutural do total de parcelas: só num contrato "intocado" (nenhum
    // pagamento confirmado e nenhum abatimento), senão a série vira inconsistência.
    const wantsTotalChange = data.totalInstallments != null
        && data.totalInstallments !== financing.totalInstallments;

    if (wantsTotalChange) {
        const confirmedRows = await db
            .select({ id: transactions.id })
            .from(transactions)
            .where(and(
                eq(transactions.financingId, financing.id),
                eq(transactions.isConfirmed, 1),
                isNull(transactions.deletedAt),
            ))
            .limit(1);
        const adjustmentRows = await db
            .select({ id: financingAdjustments.id })
            .from(financingAdjustments)
            .where(eq(financingAdjustments.financingId, financing.id))
            .limit(1);

        if (confirmedRows.length > 0 || adjustmentRows.length > 0) {
            throw new ExpectedError(i18n.t('ui:errors.totalInstallmentsLockedAfterFirst'));
        }
        if (!data.totalInstallments || data.totalInstallments < (financing.nextInstallmentNumber ?? 1)) {
            throw new ExpectedError(i18n.t('ui:errors.totalInstallmentsBelowNext'));
        }
    }

    // Curva da parcela (ADR 0081): só é tocada quando o input traz o sistema — o sheet de
    // edição sempre traz, e chamadas antigas seguem intocando o contrato.
    const wantsCurveInput = data.amortizationSystem != null;
    const nextSystem = wantsCurveInput
        ? resolveAmortizationSystem(data.amortizationSystem)
        : resolveAmortizationSystem(financing.amortizationSystem);
    const nextTotalInstallments = wantsTotalChange
        ? data.totalInstallments!
        : financing.totalInstallments;
    const regenerationStart = financing.nextInstallmentNumber ?? 1;

    const candidateRate = wantsCurveInput ? (data.annualRate ?? null) : financing.annualRate;
    const candidateBalance = wantsCurveInput ? (data.curveBalance ?? null) : financing.curveBalance;
    const candidateExtra = wantsCurveInput ? (data.curveExtraAmount ?? null) : financing.curveExtraAmount;

    // Só é mudança de curva quando ela muda o VALOR das parcelas. A comparação é sobre o
    // resultado (deriva ou não deriva, e com quais números), NÃO sobre os campos crus:
    // num contrato de parcela fixa o sheet manda o saldo devedor no campo da curva, e
    // comparar campo a campo faria todo rename de contrato pré-migração ser lido como
    // curva nova — reescrevendo as pendentes e apagando os encargos lançados nelas, que
    // é exatamente a perda de dado que a ADR 0055 corrigiu.
    const previousCurve = toCurve(financing);
    const hadCurve = hasDerivedCurve(previousCurve);
    const willHaveCurve = hasDerivedCurve({
        ...previousCurve,
        amortizationSystem: nextSystem,
        totalInstallments: nextTotalInstallments,
        annualRate: candidateRate,
        curveBalance: candidateBalance,
        curveInstallmentNumber: previousCurve.curveInstallmentNumber ?? regenerationStart,
    });
    const wantsCurveChange = wantsCurveInput && (
        hadCurve !== willHaveCurve
        || (willHaveCurve && (
            candidateRate !== previousCurve.annualRate
            || candidateBalance !== previousCurve.curveBalance
            || (candidateExtra ?? 0) !== (previousCurve.curveExtraAmount ?? 0)
        ))
    );
    // Saldo novo vale a partir da PRÓXIMA pendente — as pagas já são fato histórico.
    // Curva inalterada mantém a âncora guardada, senão a referência andaria sozinha a
    // cada edição e o valor derivado mudaria sem ninguém pedir.
    const nextCurveInstallmentNumber = wantsTotalChange
        ? regenerationStart
        : wantsCurveChange
            ? (financing.nextPendingInstallmentNumber ?? regenerationStart)
            : financing.curveInstallmentNumber;

    // Só quando o usuário mexeu no campo: reescrever a cada edição carimbaria uma data
    // nova num número que ninguém reconferiu com o banco.
    const balanceInformed = wantsCurveInput
        && candidateBalance != null
        && candidateBalance !== financing.curveBalance;

    const nextCurve: FinancingCurve = {
        amortizationSystem: nextSystem,
        monthlyAmount: data.monthlyAmount,
        totalInstallments: nextTotalInstallments,
        annualRate: candidateRate,
        curveBalance: candidateBalance,
        curveInstallmentNumber: nextCurveInstallmentNumber,
        curveExtraAmount: candidateExtra,
    };

    await db
        .update(financings)
        .set({
            name,
            ...(data.kind ? { kind: data.kind } : {}),
            institution: normalizeOptionalId(data.institution) ?? null,
            paymentAccountId,
            monthlyAmount: data.monthlyAmount,
            ...(wantsTotalChange ? { totalInstallments: data.totalInstallments! } : {}),
            ...(nextRefs ? { categoryId: nextCategoryId, subcategoryId: nextSubcategoryId } : {}),
            ...(wantsCurveInput || wantsTotalChange
                ? {
                    amortizationSystem: nextCurve.amortizationSystem!,
                    annualRate: nextCurve.annualRate,
                    curveBalance: nextCurve.curveBalance,
                    curveInstallmentNumber: nextCurve.curveInstallmentNumber,
                    curveExtraAmount: nextCurve.curveExtraAmount,
                }
                : {}),
            // O saldo informado anda junto com o da curva. São o mesmo número para o
            // usuário — o form pede uma vez só — e deixar só o da curva viajar fazia a
            // edição não ter efeito nenhum sobre o valor exibido no detalhe: a pessoa
            // digitava o saldo novo do banco e a tela continuava com o do cadastro.
            ...(balanceInformed
                ? { lastKnownBalance: candidateBalance, balanceUpdatedAt: format(new Date(), 'yyyy-MM-dd') }
                : {}),
            updatedAt: now,
        })
        .where(eq(financings.id, financing.id));

    // A categoria acompanha o contrato inteiro (pagas incluídas) — ver UpdateFinancingInput.
    const categoryPatch = nextRefs
        ? { categoryId: nextCategoryId, subcategoryId: nextSubcategoryId }
        : {};

    // Parcelas pendentes recebem conta/nome sempre; o VALOR só é reescrito quando o
    // valor mensal do contrato mudou de fato. Reescrever sempre apagava, a cada edição
    // boba (renomear o contrato), o restante de um pagamento parcial e os encargos
    // lançados na parcela — dinheiro que existe, sumindo sem aviso.
    const wantsMonthlyChange = Math.abs(data.monthlyAmount - financing.monthlyAmount) >= 0.005;
    const pendingPatch = {
        description: name,
        accountId: paymentAccountId,
        ...categoryPatch,
    };

    if ((wantsMonthlyChange || wantsCurveChange) && !wantsTotalChange) {
        // Novo valor de contrato descontando o que já foi pago parcialmente em cada
        // parcela. Com curva o valor sai por número de parcela; sem curva, achatado.
        await applyPendingInstallmentAmounts(financing.id, nextCurve, now, pendingPatch);
    } else {
        await db
            .update(transactions)
            .set({ ...pendingPatch, updatedAt: now })
            .where(and(
                eq(transactions.financingId, financing.id),
                eq(transactions.financingEntryType, FinancingEntryType.REGULAR_INSTALLMENT),
                eq(transactions.isConfirmed, 0),
                isNull(transactions.deletedAt),
            ));
    }

    // Parcelas pagas mantêm valor e conta históricos; nome e categoria acompanham.
    await db
        .update(transactions)
        .set({ description: name, ...categoryPatch, updatedAt: now })
        .where(and(
            eq(transactions.financingId, financing.id),
            eq(transactions.financingEntryType, FinancingEntryType.REGULAR_INSTALLMENT),
            eq(transactions.isConfirmed, 1),
            isNull(transactions.deletedAt),
        ));

    await db
        .update(transactions)
        .set({ description: `${name} - Abatimento`, ...categoryPatch, updatedAt: now })
        .where(and(
            eq(transactions.financingId, financing.id),
            eq(transactions.financingEntryType, FinancingEntryType.EXTRA_PREPAYMENT),
            isNull(transactions.deletedAt),
        ));

    // Pagamentos parciais seguem a mesma regra: nome e categoria são do CONTRATO.
    // Sem isto, metade da série ficaria na categoria antiga depois de uma troca — o
    // erro que `UpdateFinancingInput` existe para evitar.
    await db
        .update(transactions)
        .set({ description: `${name} - Pagamento parcial`, ...categoryPatch, updatedAt: now })
        .where(and(
            eq(transactions.financingId, financing.id),
            eq(transactions.financingEntryType, FinancingEntryType.PARTIAL_PAYMENT),
            isNull(transactions.deletedAt),
        ));

    if (wantsTotalChange) {
        // Contrato intocado: regenera a série inteira (todas as parcelas são
        // pendentes), partindo da data informada ou do vencimento atual.
        await db
            .update(transactions)
            .set({ deletedAt: now, updatedAt: now })
            .where(and(
                eq(transactions.financingId, financing.id),
                eq(transactions.financingEntryType, FinancingEntryType.REGULAR_INSTALLMENT),
                isNull(transactions.deletedAt),
            ));

        const baseDueDate = data.nextDueDate
            ?? financing.nextPendingDueDate
            ?? financing.nextDueDate;
        if (!baseDueDate) {
            throw new ExpectedError(i18n.t('ui:errors.nextDueDateRequiredRegen'));
        }
        const seriesStart = regenerationStart;
        const transactionRows = [];
        for (let installmentNumber = seriesStart; installmentNumber <= data.totalInstallments!; installmentNumber += 1) {
            const offset = installmentNumber - seriesStart;
            transactionRows.push({
                id: generateId(),
                type: 'expense',
                amount: getContractInstallmentAmount(nextCurve, installmentNumber),
                description: name,
                date: formatInstallmentDate(baseDueDate, offset),
                time: null,
                notes: null,
                accountId: paymentAccountId,
                creditCardId: null,
                invoiceId: null,
                transferDestinationAccountId: null,
                transferId: null,
                // Categoria resolvida do input (cai na do contrato quando não veio) —
                // `financing` foi lido antes do update e teria a categoria antiga.
                categoryId: nextCategoryId,
                subcategoryId: nextSubcategoryId,
                isRecurring: 0,
                recurringId: null,
                installmentId: null,
                installmentNumber: null,
                installmentTotal: null,
                financingId: financing.id,
                financingEntryType: FinancingEntryType.REGULAR_INSTALLMENT,
                financingInstallmentNumber: installmentNumber,
                adjustmentType: null,
                isConfirmed: 0,
                isHiddenFromSubs: 0,
                createdAt: now,
                updatedAt: now,
                deletedAt: null,
            });
        }
        if (transactionRows.length > 0) {
            await db.insert(transactions).values(transactionRows);
        }
    } else if (data.nextDueDate && data.nextDueDate !== financing.nextPendingDueDate) {
        // Nova data de vencimento: re-data as parcelas pendentes em sequência
        // mensal a partir dela (as pagas mantêm a data histórica).
        const pendingRows = (await getRegularScheduleRows(financing.id))
            .filter((row) => row.isConfirmed === 0)
            .sort((a, b) => (a.financingInstallmentNumber ?? 0) - (b.financingInstallmentNumber ?? 0));

        for (let index = 0; index < pendingRows.length; index += 1) {
            const nextDate = formatInstallmentDate(data.nextDueDate, index);
            if (pendingRows[index].date === nextDate) continue;
            await db
                .update(transactions)
                .set({ date: nextDate, updatedAt: now })
                .where(eq(transactions.id, pendingRows[index].id));
        }
    }

    // Depois da (eventual) regeneração, para as novas parcelas já nascerem no
    // conjunto; o sync abaixo não remove tag (só propaga união), então o
    // replace precisa acontecer aqui.
    if (data.tagIds) {
        await setFinancingTags(financing.id, data.tagIds);
    }

    await syncFinancingState(financing.id);

    return (await getFinancingById(financing.id))!;
}

/**
 * Pagamento PARCIAL de uma parcela: o valor pago vira lançamento confirmado próprio
 * (`partial_payment`) e a parcela regular CONTINUA pendente, com o restante.
 *
 * É diferente de abatimento (que é dinheiro extra amortizando o contrato) e diferente
 * de quitar a parcela por um valor menor (que reescreveria o valor devido). Aqui os
 * dois fatos ficam separados: quanto entrou e quanto ainda falta naquela parcela.
 *
 * A linha pendente é a ORIGINAL (só muda de valor) — é o que mantém o vínculo com o
 * abatimento que a removeu, a re-datação por vencimento e a numeração do contrato.
 */
export async function recordFinancingPartialPayment(data: {
    financingId: string;
    /** Parcela pendente que está sendo paga em parte. */
    transactionId: string;
    amount: number;
    date: string;
    accountId?: string | null;
}): Promise<FinancingWithRelations> {
    const financing = await getFinancingById(data.financingId);
    if (!financing) {
        throw new Error('Financiamento nao encontrado.');
    }
    if (financing.status === 'archived') {
        throw new ExpectedError(i18n.t('ui:errors.financingArchivedNoPartialPayment'));
    }

    const [installment] = await db
        .select()
        .from(transactions)
        .where(and(
            eq(transactions.id, data.transactionId),
            eq(transactions.financingId, financing.id),
            eq(transactions.financingEntryType, FinancingEntryType.REGULAR_INSTALLMENT),
            isNull(transactions.deletedAt),
        ));

    if (!installment) {
        throw new ExpectedError(i18n.t('ui:errors.financingInstallmentGone'));
    }
    if (installment.isConfirmed === 1) {
        throw new ExpectedError(i18n.t('ui:errors.financingInstallmentAlreadyPaid'));
    }

    const amount = round2(data.amount);
    if (!amount || amount <= 0) {
        throw new ExpectedError(i18n.t('ui:errors.partialPaymentAmountInvalid'));
    }
    // Pagar o valor cheio (ou mais) não é parcial — quem faz isso é o fluxo de quitação.
    if (amount >= round2(installment.amount)) {
        throw new ExpectedError(i18n.t('ui:errors.partialPaymentNotPartial'));
    }

    const accountId = normalizeOptionalId(data.accountId) ?? financing.paymentAccountId;
    const accountRows = await db
        .select({ id: accounts.id })
        .from(accounts)
        .where(and(eq(accounts.id, accountId), isNull(accounts.deletedAt)));
    if (accountRows.length === 0) {
        throw new ExpectedError(i18n.t('ui:errors.accountGone'));
    }

    const now = new Date().toISOString();
    const remaining = round2(installment.amount - amount);

    await runInTransaction(async () => {
        await db.insert(transactions).values({
            id: generateId(),
            type: 'expense',
            amount,
            description: `${financing.name} - Pagamento parcial`,
            date: data.date,
            // Pagamento parcial e abatimento são dinheiro saindo AGORA (ao contrário da
            // série de parcelas, que nasce agendada): quando a data é hoje, carimba a hora.
            time: resolveTransactionTime(data.date),
            notes: null,
            accountId,
            creditCardId: null,
            invoiceId: null,
            transferDestinationAccountId: null,
            transferId: null,
            categoryId: financing.categoryId,
            subcategoryId: financing.subcategoryId,
            isRecurring: 0,
            recurringId: null,
            installmentId: null,
            installmentNumber: null,
            installmentTotal: null,
            financingId: financing.id,
            financingEntryType: FinancingEntryType.PARTIAL_PAYMENT,
            // Mesmo número da parcela que ele abateu — é o vínculo entre os dois.
            financingInstallmentNumber: installment.financingInstallmentNumber,
            adjustmentType: null,
            isConfirmed: 1,
            isHiddenFromSubs: 0,
            createdAt: now,
            updatedAt: now,
            deletedAt: null,
        });

        await db
            .update(transactions)
            .set({ amount: remaining, updatedAt: now })
            .where(eq(transactions.id, installment.id));

        await recalculateAccountBalance(accountId);
        if (accountId !== financing.paymentAccountId) {
            await recalculateAccountBalance(financing.paymentAccountId);
        }
        await syncFinancingState(financing.id);
    });

    return (await getFinancingById(financing.id))!;
}

/**
 * Desfaz um pagamento parcial: apaga o lançamento e devolve o valor para a parcela
 * pendente correspondente.
 *
 * Três situações, e só uma bloqueia:
 * - parcela ainda PENDENTE → devolve o valor a ela (caso normal)
 * - parcela já QUITADA → bloqueia; devolver valor a uma parcela paga inflaria o total
 *   do contrato sem ninguém perceber. O usuário desfaz o pagamento dela primeiro
 * - parcela NÃO EXISTE MAIS (um abatimento por prazo removeu as últimas) → apaga só o
 *   lançamento, sem devolver nada. Bloquear aqui deixaria uma despesa confirmada
 *   impossível de excluir: `deleteTransaction` recusa qualquer linha com financingId
 */
export async function deleteFinancingPartialPayment(transactionId: string): Promise<void> {
    const [payment] = await db
        .select()
        .from(transactions)
        .where(and(
            eq(transactions.id, transactionId),
            eq(transactions.financingEntryType, FinancingEntryType.PARTIAL_PAYMENT),
            isNull(transactions.deletedAt),
        ));

    if (!payment || !payment.financingId) {
        throw new ExpectedError(i18n.t('ui:errors.partialPaymentGone'));
    }

    const financing = await getFinancingById(payment.financingId);
    if (!financing) {
        throw new Error('Financiamento nao encontrado.');
    }

    // Sem filtro de isConfirmed: precisamos distinguir "quitada" (bloqueia) de
    // "não existe mais" (deixa excluir sem devolver).
    const [installment] = await db
        .select()
        .from(transactions)
        .where(and(
            eq(transactions.financingId, payment.financingId),
            eq(transactions.financingEntryType, FinancingEntryType.REGULAR_INSTALLMENT),
            eq(transactions.financingInstallmentNumber, payment.financingInstallmentNumber ?? -1),
            isNull(transactions.deletedAt),
        ));

    if (installment && installment.isConfirmed === 1) {
        throw new ExpectedError(i18n.t('ui:errors.partialPaymentInstallmentSettled'));
    }

    const now = new Date().toISOString();

    await runInTransaction(async () => {
        await db
            .update(transactions)
            .set({ deletedAt: now, updatedAt: now })
            .where(eq(transactions.id, payment.id));

        if (installment) {
            await db
                .update(transactions)
                .set({ amount: round2(installment.amount + payment.amount), updatedAt: now })
                .where(eq(transactions.id, installment.id));
        }

        if (payment.accountId) {
            await recalculateAccountBalance(payment.accountId);
        }
        await syncFinancingState(payment.financingId!);
    });
}

/**
 * Encargos (juros/multa) de UMA parcela em aberto — o caso do empréstimo que fica
 * vencido e cresce. Não há coluna nova: o valor devido da parcela passa a ser
 * `base + encargos`, e a base é derivável (`mensal - pagamentos parciais dela`),
 * então o acréscimo atual é sempre reconstituível. Por isso o input é o TOTAL de
 * encargos da parcela, não um incremento: informar 0 volta ao valor de contrato.
 */
export async function setFinancingInstallmentCharges(data: {
    financingId: string;
    transactionId: string;
    chargesAmount: number;
}): Promise<FinancingWithRelations> {
    const financing = await getFinancingById(data.financingId);
    if (!financing) {
        throw new Error('Financiamento nao encontrado.');
    }

    const [installment] = await db
        .select()
        .from(transactions)
        .where(and(
            eq(transactions.id, data.transactionId),
            eq(transactions.financingId, financing.id),
            eq(transactions.financingEntryType, FinancingEntryType.REGULAR_INSTALLMENT),
            isNull(transactions.deletedAt),
        ));

    if (!installment) {
        throw new ExpectedError(i18n.t('ui:errors.financingInstallmentGone'));
    }
    if (installment.isConfirmed === 1) {
        throw new ExpectedError(i18n.t('ui:errors.chargesOnPaidInstallment'));
    }

    const charges = round2(data.chargesAmount);
    if (charges < 0) {
        throw new ExpectedError(i18n.t('ui:errors.chargesNegative'));
    }

    const partialPaid = await getPartialPaidByInstallment(financing.id);
    const alreadyPaid = installment.financingInstallmentNumber != null
        ? (partialPaid.get(installment.financingInstallmentNumber) ?? 0)
        : 0;
    // Base é o valor de contrato DAQUELA parcela: num contrato SAC a parcela 300 vale bem
    // menos que a de referência, e usar o valor mensal cobraria encargo sobre um número
    // que aquela parcela nunca deveu.
    const contractAmount = installment.financingInstallmentNumber != null
        ? getContractInstallmentAmount(toCurve(financing), installment.financingInstallmentNumber)
        : financing.monthlyAmount;
    const base = getInstallmentBaseAmount(contractAmount, alreadyPaid);
    const nextAmount = round2(base + charges);

    if (nextAmount <= 0) {
        throw new ExpectedError(i18n.t('ui:errors.installmentAmountInvalid'));
    }

    const now = new Date().toISOString();
    // Sem syncFinancingState: o estado derivado do contrato é prazo/próximo vencimento
    // (`buildDerivedState`), e mudar o VALOR de uma parcela não mexe em nenhum dos três —
    // seria uma releitura completa do cronograma e das tags à toa.
    await db
        .update(transactions)
        .set({ amount: nextAmount, updatedAt: now })
        .where(eq(transactions.id, installment.id));

    return (await getFinancingById(financing.id))!;
}

/**
 * **Recalibração** (ADR 0081): o boleto veio R$ X — desloca a curva inteira para bater
 * com ele, guardando a diferença como **Seguros e taxas**.
 *
 * É o que absorve TR, MIP/DFI e taxa de administração, que não estão na fórmula do SAC e
 * fariam a derivação pura sair sistematicamente abaixo do boleto. O adicional é
 * SUBSTITUÍDO, não acumulado: recalibrar duas vezes converge, não deriva.
 *
 * Deliberadamente explícito, nunca automático: antecipação com desconto e encargo de
 * atraso chegam pelo mesmo caminho (o valor liquidado diferente do previsto), e um mês
 * atípico deslocaria o futuro inteiro em silêncio. Quem decide é o usuário.
 *
 * Só reescreve parcela PENDENTE — valor de parcela paga é fato histórico.
 */
export async function recalibrateFinancingCurve(data: {
    financingId: string;
    installmentNumber: number;
    observedAmount: number;
}): Promise<FinancingWithRelations> {
    const financing = await getFinancingById(data.financingId);
    if (!financing) {
        throw new Error('Financiamento nao encontrado.');
    }

    // Mesma guarda dos demais mutadores: contrato arquivado não tem série viva pra ajustar.
    if (financing.status === 'archived') {
        throw new ExpectedError(i18n.t('ui:errors.financingArchivedNoRecalibrate'));
    }

    const curve = toCurve(financing);
    const extraAmount = recalibrateExtraAmount(curve, data.installmentNumber, data.observedAmount);
    if (extraAmount == null) {
        throw new ExpectedError(i18n.t('ui:errors.financingCurveRequiredToRecalibrate'));
    }

    const now = new Date().toISOString();
    const nextCurve: FinancingCurve = { ...curve, curveExtraAmount: extraAmount };

    // O parâmetro e a série têm que virar juntos: falha entre os dois deixaria o contrato
    // dizendo um adicional que as parcelas não têm, e a UI avisando "projeção ajustada".
    await runInTransaction(async () => {
        await db
            .update(financings)
            .set({ curveExtraAmount: extraAmount, updatedAt: now })
            .where(eq(financings.id, financing.id));

        await applyPendingInstallmentAmounts(financing.id, nextCurve, now);
    });

    return (await getFinancingById(financing.id))!;
}

/**
 * Registra um pagamento extra e decide o que ele faz com a série pendente, conforme
 * `reductionMode` (ver `FinancingReductionMode`). Em todos os modos o abatimento vira
 * um evento no razão do contrato e, salvo `withoutCashMovement`, uma despesa confirmada.
 */
export async function recordFinancingPrepayment(
    data: RecordFinancingPrepaymentInput,
): Promise<FinancingWithRelations> {
    const financing = await getFinancingById(data.financingId);
    if (!financing) {
        throw new Error('Financiamento nao encontrado.');
    }

    if (financing.status === 'archived') {
        throw new ExpectedError(i18n.t('ui:errors.financingArchivedNoPaydown'));
    }

    const pendingRegularRows = (await getRegularScheduleRows(financing.id))
        .filter((row) => row.isConfirmed === 0);

    if (pendingRegularRows.length === 0) {
        throw new ExpectedError(i18n.t('ui:errors.noPendingInstallmentsToPayDown'));
    }

    const mode: FinancingReductionMode = data.reductionMode
        ?? (data.newMonthlyAmount != null ? 'installment' : 'term');
    const isInstallmentReduction = mode === 'installment';

    // Parcelas que ESTE abatimento tira da série.
    let idsToDelete: string[] = [];
    // Só no modo advance: a primeira parcela que sobrevive fica parcialmente coberta.
    let advancePlan: AdvancePlan | null = null;
    // Com quantas parcelas o contrato fica — é o que o histórico do abatimento guarda.
    let adjustmentRemaining: number;

    if (isInstallmentReduction) {
        if (!data.newMonthlyAmount || data.newMonthlyAmount <= 0) {
            throw new ExpectedError(i18n.t('ui:errors.paydownInstallmentAmountInvalid'));
        }
        adjustmentRemaining = pendingRegularRows.length;
    } else if (mode === 'advance') {
        advancePlan = planAdvanceOnNextInstallments(pendingRegularRows, data.amount);
        // Sobrou valor depois de cobrir todas as pendentes: pagou mais do que o contrato
        // deve. Gravar assim engoliria a diferença em silêncio — nem vira parcela, nem
        // aparece em lugar nenhum —, então bloqueia e devolve a conta pro usuário.
        if (advancePlan.leftover > 0) {
            throw new ExpectedError(i18n.t('ui:errors.advanceExceedsPendingTotal'));
        }
        idsToDelete = advancePlan.settledIds;
        adjustmentRemaining = pendingRegularRows.length - idsToDelete.length;
    } else {
        if (data.remainingInstallments > pendingRegularRows.length) {
            throw new ExpectedError(i18n.t('ui:errors.remainingInstallmentsExceedSeries'));
        }

        const protectedRows = pendingRegularRows.filter((row) => row.date <= data.date);
        if (data.remainingInstallments < protectedRows.length) {
            throw new ExpectedError(i18n.t('ui:errors.paydownWouldRemoveDueInstallments'));
        }

        const deleteCount = pendingRegularRows.length - data.remainingInstallments;
        const removableRows = pendingRegularRows.filter((row) => row.date > data.date);

        if (deleteCount > removableRows.length) {
            throw new ExpectedError(i18n.t('ui:errors.paydownWouldRemoveDueInstallments'));
        }

        idsToDelete = [...removableRows]
            .sort((left, right) =>
                (right.financingInstallmentNumber ?? 0) - (left.financingInstallmentNumber ?? 0)
                || compareIso(right.date, left.date),
            )
            .slice(0, deleteCount)
            .map((row) => row.id);
        adjustmentRemaining = data.remainingInstallments;
    }

    const now = new Date().toISOString();
    // Primeira pendente que SOBREVIVE a este abatimento: é a partir dela que o saldo
    // novo vale, então é ela a âncora da curva re-derivada no modo "reduzir parcela".
    // Set + uma passada: `getRegularScheduleRows` já devolve ordenado por vencimento, então
    // a primeira sobrevivente é a resposta. Com `includes` num contrato de 360 parcelas isto
    // virava dezenas de milhares de comparações na thread JS dentro da transação.
    const deletedIdSet = new Set(idsToDelete);
    const nextPendingAfterPaydown = pendingRegularRows
        .find((row) => !deletedIdSet.has(row.id) && row.financingInstallmentNumber != null)
        ?.financingInstallmentNumber
        ?? financing.curveInstallmentNumber;
    // Sem movimentação de conta (FGTS etc.): o abatimento vive só no razão do
    // financiamento — nenhuma despesa é lançada e nenhum saldo é recalculado.
    const skipCashMovement = data.withoutCashMovement === true;
    const transactionId = skipCashMovement ? null : generateId();
    // A despesa do abatimento e o EVENTO (financing_adjustments) têm que nascer juntos:
    // crash entre os dois criava um lançamento confirmado fantasma que a UI não deixa
    // excluir (deleteTransaction bloqueia financingId) e que o financiamento não conhece.
    await runInTransaction(async () => {
        if (transactionId) {
            await db.insert(transactions).values({
                id: transactionId,
                type: 'expense',
                amount: data.amount,
                description: `${financing.name} - Abatimento`,
                date: data.date,
                time: resolveTransactionTime(data.date),
                notes: normalizeOptionalId(data.notes) ?? null,
                accountId: financing.paymentAccountId,
                creditCardId: null,
                invoiceId: null,
                transferDestinationAccountId: null,
                transferId: null,
                categoryId: financing.categoryId,
                subcategoryId: financing.subcategoryId,
                isRecurring: 0,
                recurringId: null,
                installmentId: null,
                installmentNumber: null,
                installmentTotal: null,
                financingId: financing.id,
                financingEntryType: FinancingEntryType.EXTRA_PREPAYMENT,
                financingInstallmentNumber: null,
                adjustmentType: null,
                isConfirmed: 1,
                isHiddenFromSubs: 0,
                createdAt: now,
                updatedAt: now,
                deletedAt: null,
            });
        }

        await db.insert(financingAdjustments).values({
            id: generateId(),
            financingId: financing.id,
            date: data.date,
            amount: data.amount,
            newOutstandingBalance: data.newOutstandingBalance,
            remainingInstallments: adjustmentRemaining,
            notes: normalizeOptionalId(data.notes) ?? null,
            transactionId,
            createdAt: now,
        });

        if (isInstallmentReduction) {
            // Desconta o que já foi pago parcialmente em cada parcela — senão o novo
            // valor mensal ressuscitaria a parte da parcela que o usuário já quitou.
            //
            // Com curva o banco não achata a série: ele re-deriva a partir do novo saldo,
            // e a âncora anda para a próxima pendente (é dela em diante que o saldo vale).
            // Achatar aqui destruiria a curva num toque.
            await applyPendingInstallmentAmounts(financing.id, {
                ...toCurve(financing),
                monthlyAmount: data.newMonthlyAmount!,
                curveBalance: data.newOutstandingBalance,
                curveInstallmentNumber: nextPendingAfterPaydown,
            }, now);
        }

        // O troco do adiantamento fica na primeira parcela que sobrou: ela passa a cobrar
        // só o que o pagamento não cobriu. Escrever o valor direto (e não via
        // `applyPendingInstallmentAmounts`) é de propósito — as parcelas seguintes têm que
        // continuar valendo o combinado do contrato.
        if (advancePlan?.partialId) {
            await db
                .update(transactions)
                .set({ amount: advancePlan.partialAmount, updatedAt: now })
                .where(eq(transactions.id, advancePlan.partialId));
        }

        if (idsToDelete.length > 0) {
            await db
                .update(transactions)
                .set({ deletedAt: now, updatedAt: now })
                .where(and(
                    inArray(transactions.id, idsToDelete),
                    isNull(transactions.deletedAt),
                ));
        }

        await db
            .update(financings)
            .set({
                lastKnownBalance: data.newOutstandingBalance,
                balanceUpdatedAt: data.date,
                ...(isInstallmentReduction
                    ? {
                        monthlyAmount: data.newMonthlyAmount!,
                        // A curva acompanha: novo saldo, nova âncora. No modo "reduzir
                        // prazo" ela fica intacta de propósito — lá a amortização mensal
                        // não muda, só somem parcelas do fim da série.
                        curveBalance: data.newOutstandingBalance,
                        curveInstallmentNumber: nextPendingAfterPaydown,
                    }
                    : {}),
                updatedAt: now,
            })
            .where(eq(financings.id, financing.id));

        if (!skipCashMovement) {
            await recalculateAccountBalance(financing.paymentAccountId);
        }
        await syncFinancingState(financing.id);
    });

    return (await getFinancingById(financing.id))!;
}

/**
 * Desfaz um abatimento: apaga o lançamento extra, restaura as parcelas que ELE
 * removeu e refaz o saldo devedor a partir do abatimento mais recente que sobrar.
 *
 * A restauração é exata porque `recordFinancingPrepayment` soft-deleta as parcelas
 * com o MESMO timestamp gravado em `financing_adjustments.createdAt` — esse vínculo
 * identifica quais linhas pertencem a cada abatimento.
 *
 * O que NÃO volta é VALOR de parcela: nem o das pendentes reescritas por "reduzir
 * parcela", nem o troco deixado na primeira sobrevivente por "abater das próximas".
 * Não há de onde tirar o número antigo — por isso os dois modos gravam nas notas do
 * abatimento de onde pra onde o valor foi.
 */
export async function deleteFinancingAdjustment(adjustmentId: string): Promise<void> {
    const [adjustment] = await db
        .select()
        .from(financingAdjustments)
        .where(eq(financingAdjustments.id, adjustmentId));

    if (!adjustment) {
        throw new Error('Abatimento nao encontrado.');
    }

    const financing = await getFinancingById(adjustment.financingId);
    if (!financing) {
        throw new Error('Financiamento nao encontrado.');
    }

    if (financing.status === 'archived') {
        throw new ExpectedError(i18n.t('ui:errors.financingArchivedNoUndo'));
    }

    const now = new Date().toISOString();

    // Restaura as parcelas regulares removidas por ESTE abatimento.
    await db
        .update(transactions)
        .set({ deletedAt: null, updatedAt: now })
        .where(and(
            eq(transactions.financingId, financing.id),
            eq(transactions.financingEntryType, FinancingEntryType.REGULAR_INSTALLMENT),
            eq(transactions.deletedAt, adjustment.createdAt),
        ));

    // Remove o lançamento do pagamento extra.
    if (adjustment.transactionId) {
        await db
            .update(transactions)
            .set({ deletedAt: now, updatedAt: now })
            .where(and(
                eq(transactions.id, adjustment.transactionId),
                isNull(transactions.deletedAt),
            ));
    }

    await db
        .delete(financingAdjustments)
        .where(eq(financingAdjustments.id, adjustmentId));

    // O saldo devedor volta a refletir o abatimento mais recente que restou; sem
    // nenhum, fica vazio (o snapshot informado na criação não é preservado).
    const [latestRemaining] = await db
        .select()
        .from(financingAdjustments)
        .where(eq(financingAdjustments.financingId, financing.id))
        .orderBy(desc(financingAdjustments.date), desc(financingAdjustments.createdAt))
        .limit(1);

    await db
        .update(financings)
        .set({
            lastKnownBalance: latestRemaining?.newOutstandingBalance ?? null,
            balanceUpdatedAt: latestRemaining?.date ?? null,
            updatedAt: now,
        })
        .where(eq(financings.id, financing.id));

    await recalculateAccountBalance(financing.paymentAccountId);
    await syncFinancingState(financing.id);
}

/**
 * Exclusão TOTAL do financiamento — diferente de arquivar, não preserva nada:
 * soft-delete de TODOS os lançamentos vinculados (parcelas regulares e abatimentos,
 * confirmados ou não), remove os eventos de abatimento e o contrato em si
 * (`financings` não tem deletedAt). O saldo da conta é recalculado, então o efeito
 * das parcelas já pagas é desfeito.
 */
export async function deleteFinancing(financingId: string): Promise<void> {
    const financing = await getFinancingById(financingId);
    if (!financing) return;

    const now = new Date().toISOString();

    // Um pagamento parcial pode ter saído de OUTRA conta (o sheet deixa escolher), então
    // recalcular só a conta do contrato deixaria a outra com o valor de uma despesa que
    // acabou de ser apagada.
    const affectedAccountRows = await db
        .selectDistinct({ accountId: transactions.accountId })
        .from(transactions)
        .where(and(
            eq(transactions.financingId, financingId),
            eq(transactions.isConfirmed, 1),
            isNull(transactions.deletedAt),
        ));
    const accountIdsToRecalculate = new Set<string>([financing.paymentAccountId]);
    for (const row of affectedAccountRows) {
        if (row.accountId) accountIdsToRecalculate.add(row.accountId);
    }

    await db
        .update(transactions)
        .set({ deletedAt: now, updatedAt: now })
        .where(and(
            eq(transactions.financingId, financingId),
            isNull(transactions.deletedAt),
        ));

    // Desvincula TODAS as transações do contrato (inclusive as já soft-deletadas):
    // transactions.financingId referencia financings(id) e o hard delete abaixo
    // violaria a foreign key se algum vínculo sobrasse.
    await db
        .update(transactions)
        .set({ financingId: null, financingEntryType: null, financingInstallmentNumber: null })
        .where(eq(transactions.financingId, financingId));

    await db.delete(financingAdjustments).where(eq(financingAdjustments.financingId, financingId));
    await db.delete(financings).where(eq(financings.id, financingId));

    for (const accountId of accountIdsToRecalculate) {
        await recalculateAccountBalance(accountId);
    }
}

export async function archiveFinancing(financingId: string): Promise<void> {
    const financing = await getFinancingById(financingId);
    if (!financing) return;

    const now = new Date().toISOString();

    await db
        .update(transactions)
        .set({ deletedAt: now, updatedAt: now })
        .where(and(
            eq(transactions.financingId, financingId),
            eq(transactions.financingEntryType, FinancingEntryType.REGULAR_INSTALLMENT),
            eq(transactions.isConfirmed, 0),
            isNull(transactions.deletedAt),
        ));

    await db
        .update(financings)
        .set({
            status: 'archived',
            nextInstallmentNumber: null,
            nextDueDate: null,
            updatedAt: now,
        })
        .where(eq(financings.id, financingId));
}
